import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNavBar } from '../../hooks/useNavBar';
import styles from './NavBar.module.css';

const NavBar = ({
    size = "Desktop 1440",
    hideBottomSeparator = false,
    tickerTextId = 1,
    overrideTickerText = ""
}) => {
    const tickerRef = useRef(null);
    const contentRef = useRef(null);
    const [contentWidth, setContentWidth] = useState(0);
    const navigate = useNavigate();

    const {
        logoUrl,
        tickerText,
        error
    } = useNavBar({
        size,
        tickerTextId,
        overrideTickerText
    });

    useEffect(() => {
        if (contentRef.current) {
            setContentWidth(contentRef.current.offsetWidth);
        }
    }, [tickerText]);

    useEffect(() => {
        if (tickerRef.current && contentWidth > 0) {
            const animation = tickerRef.current.animate(
                [
                    { transform: "translateX(0)" },
                    { transform: `translateX(-${contentWidth}px)` },
                ],
                {
                    duration: 80000,
                    iterations: Infinity,
                    easing: "linear",
                }
            );

            return () => {
                animation.cancel();
            }
        }
    }, [contentWidth]);

    const handleMouseEnter = useCallback(() => {
        if (tickerRef.current) {
            tickerRef.current.getAnimations()[0].playbackRate = 0.6;
        }
    }, []);

    const handleMouseLeave = useCallback(() => {
        if (tickerRef.current) {
            tickerRef.current.getAnimations()[0].playbackRate = 1;
        }
    }, []);

    const handleLogoClick = useCallback(() => {
        navigate('/');
    }, [navigate]);

    const getHorizontalWidth = useCallback(() => {
        switch (size) {
            case "Desktop 1440":
                return 1210;
            case "Desktop 1200":
                return 1057;
            case "Tablet":
                return 626;
            case "Phone":
                return 362.4;
            default:
                return 1210;
        }
    }, [size]);

    const getContainerWidths = useCallback(() => {
        switch (size) {
            case "Desktop 1440":
                return { logo: "50%", ticker: "50%" };
            case "Desktop 1200":
                return { logo: "40%", ticker: "60%" };
            case "Tablet":
                return { logo: "30%", ticker: "70%" };
            case "Phone":
                return { logo: "15%", ticker: "85%" };
            default:
                return { logo: "50%", ticker: "50%" };
        }
    }, [size]);

    if (error) {
        console.error('Error loading navbar assets:', error);
    }

    const { logo: logoWidth, ticker: tickerWidth } = getContainerWidths();

    const tickerContent = (
        <span className={styles.tickerText}>{tickerText}</span>
    );

    return (
        <div className={styles.navbar}>
            <div className={styles.content} style={{ width: getHorizontalWidth() }}>
                <div className={styles.logoContainer} style={{ width: logoWidth }}>
                    {logoUrl && (
                        <img
                            src={logoUrl}
                            alt="Logo"
                            className={`${styles.logo} ${["Tablet", "Phone"].includes(size) ? styles.small : styles.large}`}
                            onClick={handleLogoClick}
                        />
                    )}
                </div>
                <div
                    className={styles.tickerContainer}
                    style={{ width: tickerWidth }}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    <div className={styles.gradientLeft} />
                    <div className={styles.gradientRight} />
                    <div className={styles.ticker} ref={tickerRef}>
                        <div ref={contentRef} className={styles.tickerContent}>
                            {tickerContent}
                            {tickerContent}
                            {tickerContent}
                            {tickerContent}
                        </div>
                        <div className={styles.tickerContent}>
                            {tickerContent}
                            {tickerContent}
                            {tickerContent}
                            {tickerContent}
                        </div>
                    </div>
                </div>
            </div>
            {!hideBottomSeparator && <div className={styles.bottomSeparator} />}
        </div>
    );
};

export default NavBar;